import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/client';

function fileToUrl(filePath) {
  if (!filePath) return null;
  return `/uploads/staff/${String(filePath).split('/').pop()}`;
}

export default function StaffDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);

  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/staff/${id}`);
      setStaff(res.data.staff);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleEnableLogin() {
    const email = window.prompt('Enter this staff member\'s email — they\'ll receive a link to set up their Teacher app password:');
    if (!email) return;
    setLoginError('');
    setLoginBusy(true);
    try {
      await api.patch(`/staff/${id}/enable-login`, { email });
      await load();
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Failed to enable app login');
    } finally {
      setLoginBusy(false);
    }
  }

  async function handleDisableLogin() {
    if (!window.confirm('Disable this staff member\'s Teacher app login?')) return;
    setLoginError('');
    setLoginBusy(true);
    try {
      await api.patch(`/staff/${id}/disable-login`);
      await load();
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Failed to disable app login');
    } finally {
      setLoginBusy(false);
    }
  }

  if (loading) return <Layout role="schoolAdmin"><div>Loading...</div></Layout>;
  if (!staff) return <Layout role="schoolAdmin"><div>Staff member not found. <Link to="/staff">Back to Staff Management</Link></div></Layout>;

  const designationDisplay = staff.designation === 'Other' && staff.custom_designation ? staff.custom_designation : staff.designation;
  const photoSrc = fileToUrl(staff.photo_path);

  return (
    <Layout role="schoolAdmin">
      <div className="page-header">
        <div>
          <h1 className="page-title">Staff Details</h1>
          <nav style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            <Link to="/staff" style={{ color: 'var(--primary)' }}>Staff Management</Link> › <span>{staff.staff_name}</span>
          </nav>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline" onClick={() => navigate(`/staff/${id}/edit`)}><i className="fas fa-edit"></i> Edit</button>
          <button className="btn btn-outline" onClick={() => navigate('/staff')}><i className="fas fa-arrow-left"></i> Back</button>
        </div>
      </div>

      <div className="profile-card" style={{ display: 'flex', alignItems: 'center', gap: 20, background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 24 }}>
        {photoSrc ? (
          <img src={photoSrc} alt={staff.staff_name} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, flexShrink: 0 }}>
            {(staff.staff_name || '?').charAt(0)}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 19 }}>{staff.staff_name}</h2>
          <p style={{ margin: '2px 0', color: 'var(--text-secondary)', fontSize: 13 }}>Mobile: {staff.mobile_number}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <span style={{ background: 'var(--bg-secondary)', padding: '4px 12px', borderRadius: 14, fontSize: 12 }}>{designationDisplay}</span>
            <span style={{ background: 'var(--bg-secondary)', padding: '4px 12px', borderRadius: 14, fontSize: 12 }}>{staff.staff_type === 'teaching' ? 'Teaching' : 'Non-Teaching'}</span>
            <span className={`badge ${staff.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>{staff.status === 'active' ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title"><i className="fas fa-id-badge"></i> Staff Information</h3></div>
        <div style={{ padding: 16 }}>
          <DetailGrid items={[
            ['Staff Name', staff.staff_name], ['Mobile Number', staff.mobile_number],
            ['Designation', designationDisplay], ['Staff Type', staff.staff_type === 'teaching' ? 'Teaching' : 'Non-Teaching'],
            ['Class Teacher — Standard', staff.assigned_standard || '-'], ['Class Teacher — Division', staff.assigned_division || '-'],
            ['Status', staff.status === 'active' ? 'Active' : 'Inactive'],
            ['Created', staff.created_at ? new Date(staff.created_at).toLocaleDateString('en-IN') : '-'],
            ['Last Updated', staff.updated_at ? new Date(staff.updated_at).toLocaleDateString('en-IN') : '-'],
          ]} />
        </div>
      </div>

      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header"><h3 className="card-title"><i className="fas fa-mobile-alt"></i> Teacher App Login</h3></div>
        <div style={{ padding: 16 }}>
          {loginError && <div style={{ background: '#FEE2E2', color: 'var(--danger)', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{loginError}</div>}
          {staff.user_id ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="badge badge-success">Login enabled</span>
              <button className="btn btn-outline btn-sm" disabled={loginBusy} onClick={handleDisableLogin}>
                <i className="fas fa-user-slash"></i> Disable App Login
              </button>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                This staff member can't use the Teacher app yet. Enable login to send them an invite email to set up their password.
              </p>
              <button className="btn btn-primary btn-sm" disabled={loginBusy} onClick={handleEnableLogin}>
                <i className="fas fa-user-plus"></i> {loginBusy ? 'Enabling...' : 'Enable App Login'}
              </button>
            </div>
          )}
        </div>
      </div>

      {fileToUrl(staff.signature_path) && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-header"><h3 className="card-title"><i className="fas fa-signature"></i> Signature</h3></div>
          <div style={{ padding: 16 }}>
            <img src={fileToUrl(staff.signature_path)} alt="Signature" style={{ maxHeight: 80, maxWidth: 240, objectFit: 'contain', border: '1px solid var(--border)', borderRadius: 8, padding: 8 }} />
          </div>
        </div>
      )}
    </Layout>
  );
}

function DetailGrid({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
      {items.map(([label, val]) => (
        <div key={label}>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{val || '-'}</div>
        </div>
      ))}
    </div>
  );
}
